/**
 * Logs synthesis — pure, deterministic, verbatim from source (execSeed/logsFor).
 * Same seed → same 16 rows, so the demo logs are stable across renders.
 */
import type { Integration, Version } from './types';

/** String hash → uint32 seed (h = h*31 + code). */
export function execSeed(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = (h * 31 + str.charCodeAt(i)) >>> 0;
  return h;
}

export type ExecRow = {
  id: number;
  op: string;
  dir: 'inbound' | 'outbound';
  ago: string;
  status: 'ok' | 'error';
  statusLabel: string;
  statusTone: 'success' | 'danger';
  latency: string;
  detail: string;
  ver: string;
};

export type LogsResult = {
  rows: ExecRow[];
  intg: Integration | Record<string, never>;
  live: Version;
  total: number;
  successRate: number;
  errToday: number;
};

/** Synthesize a stable execution log for a connector's live version. */
export function logsFor(
  id: string,
  intg: Integration | undefined,
  versions: Version[],
  // Backend mode: return the frame with NO rows and zeroed counters, so the
  // caller renders real data or an honest empty state. Synthesized executions
  // are an offline-demo device only — showing invented traffic (latencies,
  // "429 rate limited") in an ops tool means someone debugs a run that never
  // happened.
  synthesize = true,
): LogsResult {
  const safeIntg = intg || ({} as Record<string, never>);
  const live =
    versions.find(v => v.status === 'published') ||
    versions[0] ||
    ({ vendor: '1.0', rev: 1, execs: 0 } as Version);

  if (!synthesize) {
    return { rows: [], intg: safeIntg, live, total: 0, successRate: 100, errToday: 0 };
  }

  const ops =
    intg && intg.cls === 'mcp'
      ? ['tools/call · create', 'tools/call · search', 'tools/list', 'tools/call · update']
      : ['POST /sync · outbound', 'GET /poll · inbound', 'PATCH /update · outbound', 'GET /fetch · inbound'];

  let seed = execSeed(id + '|' + (live.rev || 1));
  const rnd = () => {
    seed = (seed * 1103515245 + 12345) >>> 0;
    return seed / 4294967296;
  };

  const rows: ExecRow[] = [];
  let mins = 2;
  for (let i = 0; i < 16; i++) {
    const r = rnd();
    const err = r > 0.88;
    const op = ops[Math.floor(rnd() * ops.length)];
    const dir: 'inbound' | 'outbound' = /inbound|search|list|GET/.test(op) ? 'inbound' : 'outbound';
    const lat = Math.round(40 + rnd() * 320);
    mins += Math.round(1 + rnd() * 55);
    const ago = mins < 60 ? mins + 'm ago' : Math.floor(mins / 60) + 'h ' + (mins % 60) + 'm ago';
    rows.push({
      id: i,
      op,
      dir,
      ago,
      status: err ? 'error' : 'ok',
      statusLabel: err ? 'error' : 'ok',
      statusTone: err ? 'danger' : 'success',
      latency: lat + 'ms',
      detail: err ? (rnd() > 0.5 ? '429 rate limited · retried' : '422 field rejected by vendor') : 'committed',
      ver: 'v1 × ' + live.vendor + ' · rev ' + (live.rev || 1),
    });
  }

  const total = live.execs || 0;
  const errCount = rows.filter(r => r.status === 'error').length;
  const successRate = Math.round((1 - errCount / rows.length) * 1000) / 10;
  return { rows, intg: safeIntg, live, total, successRate, errToday: errCount };
}
